import { clamp, hexToRgb, luminance, mixRgb, smoothstep, type Rgb } from '../../engine/math';
import type { ChromeTone } from '../../engine/types';

// The light over the garden in cool morning mist, at a clear late morning,
// and in the warm low sun of the afternoon. The scene is painted in neutral
// daylight and these colours grade it.
const STOPS = {
  skyTop: ['#BACBDB', '#C6D8E6', '#EDCB9E'],
  skyLow: ['#F1E2D8', '#EDF1EE', '#F9E0B8'],
  /** Multiplied over the scene: top of the frame, then the foreground. */
  grade: ['#E4E5F1', '#FFFAF2', '#FFE6C6'],
  gradeLow: ['#CDD2E4', '#F3EDE2', '#EFD0AE'],
  mist: ['#EEF0F7', '#F8F5EE', '#FFE4BE'],
  sun: ['#FFE0C0', '#FFF5E0', '#FFCB86'],
  /** Distant hills fading into the air. */
  haze: ['#CBD6E1', '#D7DDE0', '#EAC7A0'],
} as const;

type ColorName = keyof typeof STOPS;

export type ZenColors = Record<ColorName, Rgb>;

const NAMES = Object.keys(STOPS) as ColorName[];
const PARSED = NAMES.reduce(
  (all, name) => ({ ...all, [name]: STOPS[name].map(hexToRgb) }),
  {} as Record<ColorName, Rgb[]>,
);

/** The light over the garden through the day (0 in the morning mist, 1 in the afternoon sun). */
export const zenColors = (light: number): ZenColors => {
  const scaled = clamp(light) * 2;
  const index = Math.min(1, Math.floor(scaled));
  const amount = smoothstep(0, 1, scaled - index);
  return NAMES.reduce(
    (all, name) => ({ ...all, [name]: mixRgb(PARSED[name][index], PARSED[name][index + 1], amount) }),
    {} as ZenColors,
  );
};

/** The sky above the garden wall stays pale all day, so the header text stays dark. */
export const zenChromeTone = (progress: number): ChromeTone =>
  luminance(zenColors(progress).skyTop) > 0.31 ? 'dark' : 'light';
